import { createElement, use, type ComponentType, type ReactNode } from 'react'
import type { AsyncRouteComponent } from '@tanstack/react-router'

export function lazyPage<T extends Record<string, unknown>, K extends keyof T & string>(
  importer: () => Promise<T>,
  exportName: K,
): T[K] extends (props: infer P) => ReactNode ? AsyncRouteComponent<P> : never {
  let page: ComponentType<object> | undefined
  let failureToThrowOnRender: unknown
  let loading: Promise<void> | undefined

  const preloadWithoutRejecting = () =>
    (loading ??= importer().then(
      (module) => {
        page = module[exportName] as ComponentType<object>
      },
      (error: unknown) => {
        failureToThrowOnRender = error
      },
    ))

  function LazyPage(props: object) {
    if (!page && !failureToThrowOnRender) use(preloadWithoutRejecting())
    if (failureToThrowOnRender) throw failureToThrowOnRender
    return createElement(page as ComponentType<object>, props)
  }
  LazyPage.preload = preloadWithoutRejecting

  return LazyPage as never
}
